import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from "react";
import { Alert, Box, Button, Paper, Typography } from "@mui/material";

type State = { error: Error | null };

export class AppErrorBoundary extends Component<PropsWithChildren, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Uncaught application error", error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Box className="min-h-screen bg-stone-100 p-4 sm:p-8" role="alert">
        <Paper className="mx-auto max-w-3xl p-6 sm:p-8" elevation={0} variant="outlined">
          <Typography variant="overline" color="primary" sx={{ fontWeight: 900 }}>Protected recovery screen</Typography>
          <Typography variant="h4" component="h1" sx={{ mt: 1, fontWeight: 900 }}>The page encountered an unexpected error.</Typography>
          <Alert severity="error" sx={{ mt: 3 }}>{error.message || "Unknown application error"}</Alert>
          <Typography sx={{ color: "text.secondary", mt: 2 }}>
            Your browser session remains active. Retry the current view or reload the application if the error persists.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, mt: 3, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={this.reset}>Retry page</Button>
            <Button variant="outlined" onClick={() => window.location.reload()}>Reload application</Button>
            <Button variant="outlined" onClick={() => window.location.assign("/media-content/command-center")}>Go to Command Center</Button>
          </Box>
        </Paper>
      </Box>
    );
  }
}
